import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  HiOutlineHeart,
  HiOutlineSparkles,
  HiOutlineLightBulb,
  HiOutlineShieldCheck
} from 'react-icons/hi'
import { FiArrowRight } from 'react-icons/fi'

/* ============================================================
 *  About · 關於雲耘科技
 * ============================================================ */

const Page = styled.div`
  background: var(--color-bg);
  color: var(--color-ink);
  min-height: 100vh;
`

/* -------- Hero -------- */
const Hero = styled.section`
  position: relative;
  min-height: 80vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 8rem 1.5rem 5rem;

  @media (min-width: 1440px) {
    min-height: 78vh;
    padding: 9rem 2rem 6rem;
  }
  @media (max-width: 768px) {
    min-height: 0;
    padding: 6.5rem 1.25rem 2.75rem;
  }
`

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
`

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(27, 13, 18, 0.7) 0%,
    rgba(27, 13, 18, 0.5) 100%
  );
  z-index: 2;
`

const HeroInner = styled.div`
  position: relative;
  z-index: 3;
  max-width: var(--container-wide);
  width: 100%;
  margin: 0 auto;
  color: #fff;

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--color-primary-soft);
    font-weight: 700;
    margin-bottom: 1.25rem;
    padding: 0.55rem 1.1rem;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(8px);
    border-radius: var(--radius-pill);
  }
  h1 {
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: clamp(1.9rem, 5.5vw, 4.5rem);
    line-height: 1.1;
    font-weight: 700;
    max-width: 900px;
    margin-bottom: 1.5rem;
    letter-spacing: -0.01em;
  }
  h1 em {
    font-style: italic;
    color: #f5c6d3;
    font-weight: 500;
  }
  p {
    font-size: 1.15rem;
    line-height: 1.8;
    max-width: 620px;
    color: rgba(255, 255, 255, 0.92);
  }

  @media (max-width: 640px) {
    .tag {
      margin-bottom: 1rem;
    }
    h1 {
      margin-bottom: 1rem;
    }
    p {
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }
`

/* -------- Section -------- */
const Section = styled.section`
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 3.5rem 1.5rem;

  @media (min-width: 1440px) {
    padding: 4rem 2rem;
  }
  @media (max-width: 768px) {
    padding: 2.5rem 1.25rem;
  }
`

const SectionHead = styled.div`
  max-width: 720px;
  margin-bottom: 2.25rem;

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #d5728a;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h2 {
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: clamp(1.55rem, 3.5vw, 2.6rem);
    font-weight: 700;
    color: #1b0d12;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  p {
    font-size: 1.02rem;
    line-height: 1.8;
    color: #6b3547;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    p {
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }
`

/* -------- Mission -------- */
const MissionGrid = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  @media (max-width: 640px) {
    gap: 1.25rem;
  }
`

const MissionQuote = styled.blockquote`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.3rem, 2.5vw, 2rem);
  line-height: 1.4;
  color: var(--color-ink);
  font-weight: 500;
  font-style: italic;
  background: #fff;
  border: 1px solid var(--color-line);
  border-left: 4px solid var(--color-primary);
  border-radius: var(--radius-lg);
  padding: 2.25rem 2.25rem;
  margin: 0;
  box-shadow: var(--shadow-card);

  em {
    color: var(--color-primary);
    font-style: italic;
  }

  @media (max-width: 640px) {
    padding: 1.25rem 1.25rem;
    font-size: 1.15rem;
    border-radius: var(--radius-md);
  }
`

const MissionText = styled.div`
  p {
    font-size: 1rem;
    line-height: 1.85;
    color: #4b2b37;
    margin-bottom: 1.25rem;
  }
  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 640px) {
    p {
      font-size: 0.92rem;
      line-height: 1.75;
      margin-bottom: 0.9rem;
    }
  }
`

/* -------- Values -------- */
const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
  }
`

const ValueCard = styled(motion.div)`
  padding: 2.25rem 1.75rem;
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-lg);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--color-primary);
    box-shadow: var(--shadow-card-hover);
  }

  .icon {
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--color-bg-soft), var(--color-primary-soft));
    color: var(--color-primary);
    border-radius: var(--radius-md);
    margin-bottom: 1.5rem;
  }
  h4 {
    font-family: 'Playfair Display', serif;
    font-size: 1.2rem;
    color: #1b0d12;
    font-weight: 700;
    margin-bottom: 0.6rem;
  }
  p {
    font-size: 0.92rem;
    line-height: 1.7;
    color: #6b3547;
  }

  @media (max-width: 640px) {
    padding: 1.1rem 0.95rem;
    border-radius: var(--radius-md);
    .icon {
      width: 38px;
      height: 38px;
      margin-bottom: 0.75rem;
      border-radius: var(--radius-sm);
      svg {
        width: 18px;
        height: 18px;
      }
    }
    h4 {
      font-size: 0.95rem;
      margin-bottom: 0.3rem;
    }
    p {
      font-size: 0.78rem;
      line-height: 1.55;
    }
  }
`

/* -------- Team -------- */
const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 640px) {
    gap: 0.75rem;
  }
`

const TeamCard = styled(motion.div)`
  padding: 2.5rem 2rem;
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-lg);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--color-primary);
    box-shadow: var(--shadow-card-hover);
  }

  .title-tag {
    font-size: 0.7rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #d5728a;
    font-weight: 700;
    margin-bottom: 0.75rem;
    display: block;
  }
  h4 {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    color: #1b0d12;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }
  p {
    font-size: 0.92rem;
    line-height: 1.7;
    color: #6b3547;
  }

  @media (max-width: 640px) {
    padding: 1.25rem 1.1rem;
    border-radius: var(--radius-md);
    .title-tag {
      margin-bottom: 0.4rem;
    }
    h4 {
      font-size: 1.05rem;
      margin-bottom: 0.4rem;
    }
    p {
      font-size: 0.85rem;
      line-height: 1.65;
    }
  }
`

/* -------- CTA -------- */
const CtaWrap = styled.section`
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;

  @media (min-width: 1440px) {
    padding: 2.5rem 2rem 5rem;
  }

  @media (max-width: 640px) {
    padding: 1rem 1.25rem 2.5rem;
  }
`

const CtaInner = styled.div`
  background: linear-gradient(135deg, #d5728a 0%, #b85578 100%);
  color: #fff;
  padding: 4rem 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-heavy);

  @media (max-width: 768px) {
    padding: 2.75rem 2rem;
    border-radius: var(--radius-lg);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.4rem, 2.8vw, 2.2rem);
    font-weight: 700;
    margin-bottom: 0.75rem;
    line-height: 1.2;
  }
  p {
    font-size: 1rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.9);
    max-width: 520px;
  }

  @media (max-width: 640px) {
    padding: 2rem 1.5rem;
    gap: 1.25rem;
    p {
      font-size: 0.92rem;
    }
  }
`

const CtaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: var(--color-ink);
  border: none;
  padding: 1.05rem 2rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  border-radius: var(--radius-pill);
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 640px) {
    padding: 0.85rem 1.5rem;
    font-size: 0.92rem;
  }
`

/* ============================================================
 *  Component
 * ============================================================ */

const About = () => {
  const navigate = useNavigate()

  return (
    <Page>
      {/* Hero */}
      <Hero>
        <HeroVideo autoPlay loop muted playsInline>
          <source src='/images/hero-bg.mp4' type='video/mp4' />
        </HeroVideo>
        <HeroOverlay />
        <HeroInner>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className='tag'>About · 關於我們</span>
            <h1>
              以科技與美感，<br />
              <em>賦能每一位美業職人</em>
            </h1>
            <p>
              雲耘科技 Yunyun Tech. 是一支由女性主導的科技團隊，
              相信「美」不只是外在形式，更是一種專業、一種品牌、一份值得被系統化守護的事業。
            </p>
          </motion.div>
        </HeroInner>
      </Hero>

      {/* Mission */}
      <Section>
        <SectionHead>
          <span className='tag'>Our Mission</span>
          <h2>我們在做什麼</h2>
          <p>
            雲耘科技專注於美業 SaaS 產品開發。我們打造的 Beautefeel 美感線上預約平台，
            已成為上百位美業職人日常營運的核心系統。
          </p>
        </SectionHead>

        <MissionGrid>
          <MissionQuote>
            「讓每一位<em>專注於服務</em>的職人，<br />
            都擁有一套<em>值得托付</em>的營運系統。」
          </MissionQuote>
          <MissionText>
            <p>
              在與上百位美業工作者交流的過程裡，我們發現：大多數的店家並不缺技術、也不缺熱情，
              缺的是一個願意為他們量身打造、持續進化的數位工具。
            </p>
            <p>
              因此，雲耘科技只做一件事——
              <strong>把美業所需的預約、客戶、金流、行銷、數據，整合進一個優雅而穩定的平台裡</strong>。
              讓職人能把時間還給雙手，把心意還給顧客。
            </p>
            <p>
              我們相信，美業的下一個十年，不會是誰更便宜，而是誰更懂得用科技放大自己的專業。
            </p>
          </MissionText>
        </MissionGrid>
      </Section>

      {/* Values */}
      <Section>
        <SectionHead>
          <span className='tag'>Our Values</span>
          <h2>四個讓我們走得更遠的信念</h2>
          <p>
            這不是貼在牆上的口號，而是我們每一次開會、每一個產品決策都會回頭檢視的準則。
          </p>
        </SectionHead>

        <ValueGrid>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className='icon'>
              <HiOutlineHeart size={24} />
            </div>
            <h4>同理心先行</h4>
            <p>
              每一個功能都從「店家真的會在忙什麼」開始設計，不做炫技、只做能用。
            </p>
          </ValueCard>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className='icon'>
              <HiOutlineSparkles size={24} />
            </div>
            <h4>陪伴優於服務</h4>
            <p>
              我們把每一家導入 Beautefeel 的店家視為長期夥伴，
              持續回訪、持續優化，不只賣系統，更陪客戶一起經營。
            </p>
          </ValueCard>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className='icon'>
              <HiOutlineLightBulb size={24} />
            </div>
            <h4>持續進化</h4>
            <p>
              產業在變、消費者在變，我們持續聆聽客戶回饋，每月穩定更新與優化。
            </p>
          </ValueCard>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className='icon'>
              <HiOutlineShieldCheck size={24} />
            </div>
            <h4>資安為本</h4>
            <p>
              顧客資料是客戶的資產，我們以金融業等級的加密與備份策略對待它。
            </p>
          </ValueCard>
        </ValueGrid>
      </Section>

      {/* Team & Partners */}
      <Section>
        <SectionHead>
          <span className='tag'>Team & Partners</span>
          <h2>內部團隊 × 外部合作夥伴</h2>
          <p>
            我們專注於把產品做深，把客戶服務做穩。其餘需要專業度的延伸服務，
            則與長期合作的美業行銷夥伴一同交付，讓客戶獲得從系統到市場的完整支援。
          </p>
        </SectionHead>

        <TeamGrid>
          <TeamCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className='title-tag'>Product × Engineering</span>
            <h4>產品與工程團隊</h4>
            <p>
              我們的核心團隊。負責 Beautefeel 的每月迭代、系統穩定、資料安全與效能優化，
              確保平台在任何尖峰時段都能穩穩地陪著客戶營運。
            </p>
          </TeamCard>
          <TeamCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <span className='title-tag'>Customer Success</span>
            <h4>客戶成功團隊</h4>
            <p>
              不是客服，而是顧問。陪伴每一位店家完成導入、操作培訓、後台設定到長期營運優化，
              每月主動回訪並提供數據建議。
            </p>
          </TeamCard>
          <TeamCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <span className='title-tag'>Partner · Beauty Marketing</span>
            <h4>美業行銷合作團隊</h4>
            <p>
              長期合作的外部美業行銷夥伴，專精 Google / Meta 廣告投放、IG
              內容經營、SEO 佈局與沙龍品牌操作。客戶若有行銷需求，我們會媒合並共同提案。
            </p>
          </TeamCard>
        </TeamGrid>
      </Section>

      {/* CTA */}
      <CtaWrap>
        <CtaInner>
          <div>
            <h3>想更認識雲耘科技？</h3>
            <p>
              不管你是潛在客戶、媒體採訪、還是想加入我們，都歡迎直接與我們聊聊。
            </p>
          </div>
          <CtaBtn onClick={() => navigate('/contact')}>
            與我們聯繫 <FiArrowRight />
          </CtaBtn>
        </CtaInner>
      </CtaWrap>
    </Page>
  )
}

export default About
